/**
 * Headless state for the service (product) create/edit form: the fields, the
 * save-ability rule and `POST/PATCH /api/admin/products` with its error-code
 * mapping. Returns no JSX - each app renders its own form chrome.
 */
import { useState } from "react";
import { makeT } from "../i18n";
import type { ApiClient, Locale, LocaleMap, ProductDTO } from "../types";
import { cleanMap, errCodeStr } from "./shared";

export interface UseProductFormOptions {
  api: ApiClient;
  locale: Locale;
  /** `null` = create a new service. */
  product: ProductDTO | null;
  onSaved: () => void;
}

export interface UseProductFormResult {
  isNew: boolean;
  code: string;
  /** Lowercases the value - service codes are always lower case. */
  setCode: (value: string) => void;
  name: LocaleMap;
  setName: (next: LocaleMap) => void;
  description: LocaleMap;
  setDescription: (next: LocaleMap) => void;
  icon: string;
  setIcon: (value: string) => void;
  /** Content locale currently being edited (see `EDIT_LOCALES`). */
  activeLocale: string;
  setActiveLocale: (locale: string) => void;
  /** Kept as a string so the number input can be emptied while typing. */
  sortOrder: string;
  setSortOrder: (value: string) => void;
  saving: boolean;
  /** Translated message for the last failed save, or `null`. */
  error: string | null;
  canSave: boolean;
  save: () => Promise<void>;
}

export function useProductForm({ api, locale, product, onSaved }: UseProductFormOptions): UseProductFormResult {
  const t = makeT(locale);
  const isNew = product === null;
  const [code, setCodeRaw] = useState(product?.code ?? "");
  const [name, setName] = useState<LocaleMap>(() => ({ ...(product?.name ?? {}) }));
  const [description, setDescription] = useState<LocaleMap>(() => ({ ...(product?.description ?? {}) }));
  const [icon, setIcon] = useState(product?.icon ?? "");
  const [activeLocale, setActiveLocale] = useState<string>("et");
  const [sortOrder, setSortOrder] = useState(String(product?.sortOrder ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setCode(value: string) {
    setCodeRaw(value.toLowerCase());
  }

  const canSave = (isNew ? /^[a-z0-9][a-z0-9_-]*$/.test(code) : true) && (name.et ?? "").trim().length > 0 && !saving;

  async function save() {
    setSaving(true);
    setError(null);
    const cleanedDesc = cleanMap(description);
    const body = {
      ...(isNew ? { code } : {}),
      name: cleanMap(name),
      description: Object.keys(cleanedDesc).length ? cleanedDesc : undefined,
      icon: icon.trim() || undefined,
      sortOrder: parseInt(sortOrder, 10) || 0,
    };
    try {
      if (isNew) await api.post("/admin/products", body);
      else await api.patch(`/admin/products/${product!.code}`, body);
      onSaved();
    } catch (e) {
      const c = errCodeStr(e);
      setError(c === "code_taken" ? t("products.err.codeTaken") : t("products.err.generic"));
    } finally {
      setSaving(false);
    }
  }

  return {
    isNew,
    code, setCode,
    name, setName,
    description, setDescription,
    icon, setIcon,
    activeLocale, setActiveLocale,
    sortOrder, setSortOrder,
    saving, error, canSave, save,
  };
}
